import React, { useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { DefaultHeader } from '../../components/DefaultHeader';
import { LoadingContainer } from '../../components/LoadingContainer';
import ProgressTab from '../../components/ProgressTab';
import { SITE_LINKS } from '../../constants';
import { useGetLevelsWithProgress } from '../../hooks/use-get-levels-with-progress';
import { getLessonIndexPageLevelStatus } from '../../models/LessonIndexPageLevel';
import { getLessonIndexPageUnitStatus } from '../../models/LessonIndexPageUnit';
import { Background } from '../../stories/Background';
import LevelList from '../../stories/LevelList';
import { LevelListItemProps } from '../../stories/LevelListItem';
import ResponsivePageHeader from '../../stories/ResponsivePageHeader';
import { UnitListItemProps } from '../../stories/UnitListItem';

export const LessonIndexPage: React.FC = () => {
  const __ = useTranslation().t;
  const { progress, isFetching, isError } = useGetLevelsWithProgress();

  const levels: LevelListItemProps[] | undefined = useMemo(() => {
    return progress?.map((level) => {
      const unitListItems: UnitListItemProps[] = level.units?.map((unit) => {
        const unitStatus = getLessonIndexPageUnitStatus(unit);
        const buttonText = unitStatus === 'current' ? __('start') : undefined;
        return {
          imgSrc: unit.thumbSrc,
          status: unitStatus,
          title: (
            <>
              <span>{unit.name}</span>
              <span className="d-inline">: {unit.description}</span>
            </>
          ),
          buttonText,
          buttonHref: SITE_LINKS.UnitPage(level.position, unit.position),
        };
      });

      const eolTestAllowed = level.eolTestAllowed;
      const levelStatus = level.status;
      const endOfLevel: UnitListItemProps = {
        status: levelStatus === 'completed' ? 'done' : eolTestAllowed ? 'current' : 'blocked',
        title: __('endOfLevelTest'),
        buttonText: eolTestAllowed ? __('start') : undefined,
        buttonHref: SITE_LINKS.EndOfLevelTest(level.position),
        isEOL: true,
      };

      unitListItems.push(endOfLevel);

      return {
        title: level.title,
        status: getLessonIndexPageLevelStatus(level),
        unitListItems,
        buttonHref: SITE_LINKS.LevelDetailsPage(level.position),
      };
    });
  }, [progress]);

  if (isError) {
    window.location.href = SITE_LINKS.HomePage;
    return null;
  }

  return (
    <>
      <DefaultHeader activeItem={__('progress')} />
      <Background>
        <ResponsivePageHeader text={__('myProgress')} />
        <ProgressTab activeKey={__('lessonIndex')} />
        <Container>
          <div className="oejr-page-lesson-index">
            {isFetching ? <LoadingContainer /> : <LevelList levels={levels} />}
          </div>
        </Container>
      </Background>
    </>
  );
};
